import {ObjectId} from 'bson';
import {AccountWithLogo, AccountWithLogoSchema} from '../../scoredAccounts';
import {ObjectIdSchema} from '../../zod';
import {
  ApiDefinition,
  BatchedApiDefinition,
  WithIdSchema,
} from '../definitions';
import {z} from 'zod';
import {AccountQuerySchema, AccountSelectionSchema} from '../api';
import {AccountEnrichmentCreditRedemptionSchema} from '../../customer';
import {
  LegacyAiTeamSizeParamsSchema,
  FieldDefinitionAnalysisRunSchema,
} from '../../enrichment';
import {
  CreditPriceResponseSchema,
  GetModelTestResultsResponseSchema,
} from '../responses';
import {
  SamDefinitionSchema,
  ScoringModelSchema,
  MarketSchema,
} from '../../market';
import {OnboardingTemplateRequestSchema} from '../../onboarding';
import {ModelTestFieldsSchema, TierCountsSchema} from '../../scoring';
import {ModelTestDefinitionSchema} from '../../modelTest';
import {TestListSchema} from '../../testLists';
import {TestListName} from '../../validation';

// TODO: split these into separate namespaces

export const GetAccountsWithLogos: BatchedApiDefinition<
  {accountIds: ObjectId[]},
  AccountWithLogo[],
  ObjectId,
  AccountWithLogo
> = {
  path: 'accountsWithLogos',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({
    accountIds: ObjectIdSchema.array(),
  }),
  responseDataSchema: AccountWithLogoSchema.array(),
  itemSchema: ObjectIdSchema,
  itemResponseDataSchema: AccountWithLogoSchema,

  createParams: (accountIds) => ({
    accountIds,
  }),
  resolveItem: (accounts, accountId) =>
    accounts.find(({_id}) => _id.equals(accountId)) ?? null,
};

export const ClaimBonusCode = {
  path: 'claimBonusCode',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    bonusCode: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const BulkFindAccounts = {
  path: 'bulkFindAccounts',
  method: 'post', // POST instead of GET due to the potential size of the request
  options: {access: 'all'},
  requestDataSchema: z.object({
    domains: z.string().array(),
    type: z.enum(['lookalike', 'preview']),
  }),
  responseDataSchema: ObjectIdSchema.array(),
} satisfies ApiDefinition;

export const ClayExport = {
  path: 'clayExport',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    query: z.optional(AccountQuerySchema),
    webhookUrl: z.string(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const AccountEnrichmentRedemptionMaxResults = 10;
export const GetAccountEnrichmentRedemptions = {
  path: 'getAccountEnrichmentRedemptions',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({offset: z.number().min(0).optional()}),
  responseDataSchema: z.object({
    totalRedemptions: z.number(),
    redemptions: AccountEnrichmentCreditRedemptionSchema.array(),
  }),
} satisfies ApiDefinition;

export const GetIsEnrichingAgent = {
  path: 'isEnrichingAgent',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    agentId: ObjectIdSchema,
  }),
  responseDataSchema: z.boolean(),
} satisfies ApiDefinition;

export const GetNumAccountsToEnrichDefinition = {
  path: 'numAccountsToEnrich',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    accountSelection: AccountSelectionSchema,
    fieldDefinitionId: ObjectIdSchema,
    overrideFieldValues: z.boolean(),
    preview: z.boolean().optional(),
  }),
  responseDataSchema: z.object({
    creditsNeeded: z.number(),
    numAccounts: z.number(),
  }),
} satisfies ApiDefinition;

export const RunEnrichmentDefinition = {
  path: 'runEnrichment',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    accountSelection: AccountSelectionSchema,
    fieldDefinitionId: ObjectIdSchema,
    overrideFieldValues: z.boolean(),
    bypassCreditCheck: z.boolean().optional(),
    preview: z.boolean().optional(),
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetEnrichedFieldCount = {
  path: 'enrichedFieldCount',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
  }),
  responseDataSchema: z.number(),
} satisfies ApiDefinition;

export const GetLatestFieldDefinitionAnalysisRun = {
  path: 'latestFieldDefinitionAnalysisRun',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
  }),
  responseDataSchema:
    FieldDefinitionAnalysisRunSchema.extend(WithIdSchema).nullable(),
} satisfies ApiDefinition;

export const UpdateFieldDefinitionAnalysisRun = {
  path: 'updateFieldDefinitionAnalysisRun',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    testListIds: ObjectIdSchema.array(),
    bypassCreditCheck: z.boolean().optional(),
  }),
  responseDataSchema:
    FieldDefinitionAnalysisRunSchema.extend(WithIdSchema).nullable(),
} satisfies ApiDefinition;

export const GetFieldDefinitionAnalysisRunResults = {
  path: 'fieldDefinitionAnalysisRunResults',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionRunId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      domain: z.string(),
      testListLabels: z.string().array(),
      value: z.unknown(),
      reasoning: z.string().optional(),
    })
    .array(),
} satisfies ApiDefinition;

export const GetAnalysisRunCredits = {
  path: 'analysisRunCredits',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    testListIds: ObjectIdSchema.array(),
  }),
  responseDataSchema: z.number(),
} satisfies ApiDefinition;

export const GetEnrichmentPreviewResultDefinition = {
  path: 'enrichmentPreviewResult',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldDefinitionId: ObjectIdSchema,
    accountId: ObjectIdSchema,
  }),
  responseDataSchema: z
    .object({
      value: z.unknown(),
      reasoning: z.string().optional(),
      stale: z.boolean().optional(),
    })
    .nullable(),
} satisfies ApiDefinition;

export const GetTeamSizeParams = {
  path: 'teamSizeParams',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    fieldId: ObjectIdSchema,
  }),
  responseDataSchema: LegacyAiTeamSizeParamsSchema.nullable(),
} satisfies ApiDefinition;

export const GetCreditPrices = {
  path: 'creditPrices',
  method: 'get',
  options: {access: 'all'},
  requestDataSchema: z.object({}),
  responseDataSchema: CreditPriceResponseSchema,
} satisfies ApiDefinition;

export const CreateCheckoutSession = {
  path: 'createCheckoutSession',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    priceId: z.string(),
  }),
  responseDataSchema: z.object({
    url: z.string(),
  }),
} satisfies ApiDefinition;

export const PostMarket = {
  path: 'market',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: z.object({
    marketId: z.instanceof(ObjectId),
    samDefinition: SamDefinitionSchema.optional(),
    scoringModel: ScoringModelSchema.partial().optional(),
    triggerRefresh: z.boolean().optional(),
  }),
  responseDataSchema: MarketSchema,
} satisfies ApiDefinition;

export const SetOnboardingTemplate = {
  path: 'setOnboardingTemplate',
  method: 'post',
  options: {access: 'all'},
  requestDataSchema: OnboardingTemplateRequestSchema,
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetModelTestFields = {
  path: 'modelTestFields',
  method: 'get',
  options: {access: 'admin'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: ModelTestFieldsSchema,
} satisfies ApiDefinition;

export const GetModelTestResults = {
  path: 'modelTestResults',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: GetModelTestResultsResponseSchema,
} satisfies ApiDefinition;

export const GetModelTestListResults = {
  path: 'modelTestListResults',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
    listId: ObjectIdSchema,
  }),
  responseDataSchema: TierCountsSchema,
} satisfies ApiDefinition;

export const GetModelTestDefinition = {
  path: 'modelTestDefinition',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: ModelTestDefinitionSchema.extend({
    lists: z.array(TestListSchema.and(z.object(WithIdSchema))),
  }).nullable(),
} satisfies ApiDefinition;

export const MaxModelTestLists = 10;
export const MaxModelTestAccounts = 12000;
export const PostModelTestList = {
  path: 'modelTestList',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    label: z.preprocess(
      (v) => String(v).trim(),
      z
        .string()
        .regex(TestListName.pattern)
        .min(TestListName.minLength)
        .max(TestListName.maxLength)
    ),
    domains: z.string().array().max(MaxModelTestAccounts),
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const CreateModelTestControlList = {
  path: 'createModelTestControlList',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const RefreshModelTestControlList = {
  path: 'refreshModelTestControlList',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
    listId: ObjectIdSchema,
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const DeleteModelTestList = {
  path: 'deleteModelTestList',
  method: 'post',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    marketId: ObjectIdSchema,
    listId: ObjectIdSchema,
  }),
  responseDataSchema: z.void(),
} satisfies ApiDefinition;

export const GetTestLists = {
  path: 'testLists',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({}),
  responseDataSchema: TestListSchema.and(z.object(WithIdSchema)).array(),
} satisfies ApiDefinition;

export const GetTestList = {
  path: 'testList',
  method: 'get',
  options: {access: 'paid'},
  requestDataSchema: z.object({
    listId: ObjectIdSchema,
  }),
  responseDataSchema: TestListSchema.and(z.object(WithIdSchema)),
} satisfies ApiDefinition;

// TODO: move this const if we request/response types in their own file
export const MaxValidateDomainRows = 5000;
export const ValidateDomains = {
  path: 'validateDomains',
  method: 'post',
  options: {access: 'admin'},
  requestDataSchema: z.array(
    z
      .object({
        domain: z.string(),
      })
      .catchall(z.unknown())
  ),
  responseDataSchema: z
    .object({
      domain: z.string(),
      finalDomain: z.string().optional(),
      status: z.string(),
    })
    .catchall(z.unknown())
    .array(),
} satisfies ApiDefinition;
